<script>
import stockNotification from '@phoenix/mixins/components/product/stockNotification';
import SelectField from '@maison/components/form/fields/SelectField.vue';
import CheckboxField from '@maison/components/form/fields/CheckboxField.vue';
import { set } from 'lodash-es';
import PhoneTokenField from '@maison/components/form/fields/PhoneTokenField.vue';
import PhoneField from '@maison/components/form/fields/PhoneField.vue';
import ProductCardKey from '@maison/components/product/ProductCardKey.vue';
import { clientApi } from '@phoenix/clientApi';
import panelMixin from '@phoenix/mixins/panelMixin';
import SendPhoneTokenForm from '@maison/components/myAccount/SendPhoneTokenForm.vue';
import { PhxsgButton } from '@sg/src/View/Components/Atoms/Button';
import { addLoadingState, removeLoadingState } from '@sg/resources/assets/js/helpers/buttons';

export default {
  components: {
    ProductCardKey,
    CheckboxField,
    SelectField,
    PhoneTokenField,
    PhoneField,
    SendPhoneTokenForm,
    PhxsgButton,
  },

  mixins: [panelMixin, stockNotification],

  data() {
    return {
      currentStep: 'form',
      tokenRequestPending: false,
      isSendingToken: false,
      phoneToken: {
        id: 'phoneToken',
        name: 'phoneToken',
        label: this.$t('Form.label.phoneToken'),
        value: null,
        required: false,
      },
      panelOptions: {
        onShow: (event) => {
          const props = this.getPanelProps(event);
          this.initFields(props);
          this.updateContactRequirements();
        },
        onHidden: () => {
          this.hasRegistrationSuccess = false;
          this.product = null;
          this.resetStockNotificationForm();
        },
      },
      hasRegistrationSuccess: false,
      product: null,
    };
  },

  computed: {
    formPhoneNumber() {
      return this.fields.phoneNumber?.value || '';
    },

    productDescription() {
      if (!this.product) {
        return '';
      }

      return this.product.description || '';
    },

    productSecondaryDescription() {
      if (this.product.references) {
        const reference = this.product.references.find((ref) => ref.value === this.fields.reference.value);
        return [reference?.label, reference.value].filter(Boolean).join(' - ') || '';
      }

      return this.product.reference || this.product.modelReference || '';
    },

    hasProduct() {
      return this.product !== null;
    },

    isPhoneVerificationStep() {
      return this.currentStep === 'phoneVerification';
    },

    showSendTokenButton() {
      return (
        this.isPhoneVerificationEnabled && this.isCnCountry && !this.isPhoneVerified && this.currentStep === 'form'
      );
    },
  },

  watch: {
    'fields.phoneNumber.value'(value) {
      const phoneValue = value || '';
      const accountPhone = this.customerInfo?.mobilePhone || '';
      this.isPhoneVerified = !!phoneValue && phoneValue === accountPhone && this.customerInfo?.mobilePhoneVerified;

      this.tokenRequestPending = false;
    },
  },

  methods: {
    updateContactRequirements() {
      if (!this.fields.email || !this.fields.phoneNumber) {
        return;
      }

      this.fields.email.required = !this.isCnCountry;
      this.fields.phoneNumber.required = this.isCnCountry;
    },

    async onSuccess() {
      this.addTracking();

      try {
        const result = await clientApi.get(
          window.route('products.getByReference', {
            reference: this.fields.reference.value,
          }),
        );
        this.product = result.data;
      } catch {
        this.product = null;
      }
      this.hasRegistrationSuccess = true;
      this.resetStockNotificationForm();
    },

    async sendToken() {
      if (this.isSendingToken) {
        return;
      }

      this.isSendingToken = true;
      const button = this.$refs.sendTokenPhoneButton;

      try {
        const form = this.$refs.phoenixForm;
        form.setValues(form.formValues);

        const { valid } = await form.validate();
        if (!valid) {
          return;
        }

        if (button) {
          addLoadingState(button);
        }

        if (!this.tokenRequestPending) {
          const success = await this.$refs.sendPhoneTokenForm.submitForm();
          if (!success) {
            return;
          }

          this.tokenRequestPending = true;
        }
        this.currentStep = 'phoneVerification';
      } finally {
        this.isSendingToken = false;
        if (button) {
          removeLoadingState(button);
        }
      }
    },

    async verifyPhoneToken() {
      try {
        set(this.phoneToken, ['errors'], '');

        await clientApi.post(window.route('api.customer.phoneValidation.verify'), {
          phoneNumber: this.fields.phoneNumber.value,
          phoneToken: this.phoneToken.value,
        });
      } catch (error) {
        const errorMessage = error.response?.data?.errors?.phoneToken?.[0] || this.$t('Form.errors.invalidPhoneToken');
        set(this.phoneToken, ['errors'], errorMessage);

        return;
      }

      this.isPhoneVerified = true;
      this.tokenRequestPending = false;
      set(this.phoneToken, ['value'], '');

      await this.$nextTick();
      await this.$refs.phoenixForm.submit();
    },

    onResendToken() {
      this.tokenRequestPending = false;
      this.sendToken();
    },

    backToForm() {
      this.currentStep = 'form';
      this.tokenRequestPending = false;
      set(this.phoneToken, ['value'], '');
      set(this.phoneToken, ['errors'], null);
    },
  },
};
</script>

<template>
  <teleport to="#teleport-target-panels">
    <panel
      :id="id"
      ref="stockNotifyPanel"
      class="size-selector-panel"
      :title="$t('Product.stock.notifyModalTitle')"
      :options="panelOptions"
      tight
      reverse
      body-data-cy="stock-notify-container"
    >
      <template #content>
        <div v-show="!hasRegistrationSuccess && currentStep === 'form'" class="box">
          <div v-html="descriptionText"></div>

          <phoenix-form
            ref="phoenixForm"
            v-model="fields"
            class="mt-md"
            :prefill-enabled="true"
            :on-success-callback="onSuccess"
            :on-cancel-callback="close"
            :captcha-enabled="true"
            :action="action"
            :should-display-cancel-button="false"
            :should-display-submit-button="!showSendTokenButton"
            should-reset-form-on-submit
            should-display-required-text
            :terms="terms"
            :terms-link="termsLink"
            :privacy-link="privacyLink"
            submit-button-full
            data-cy="stock-notify-form"
            form-type="bis"
          >
            <template #default="{ shouldDisplayNewsletter }">
              <template v-if="isBackInStockCommerceApiEnabled">
                <form-group v-if="fieldIsPresent('gender')" :attrs="fields.gender" class="mt-md">
                  <select-field v-model="fields.gender.value" />
                </form-group>

                <form-group v-if="fieldIsPresent('title')" :attrs="fields.title" class="mt-md">
                  <select-field v-model="fields.title.value" />
                </form-group>

                <form-group :attrs="fields.firstName" class="mt-md">
                  <text-field v-model="fields.firstName.value" />
                </form-group>

                <form-group :attrs="fields.lastName" class="mt-md">
                  <text-field v-model="fields.lastName.value" />
                </form-group>
              </template>

              <form-group :attrs="fields.email" class="mt-md">
                <text-field v-model="fields.email.value" />
              </form-group>

              <form-group :attrs="fields.phoneNumber" class="mt-md">
                <phone-field v-model="fields.phoneNumber.value" />

                <div
                  v-if="isPhoneVerificationEnabled && isPhoneVerified"
                  class="field__criterion field__criterion--valid"
                >
                  {{ $t('Form.backInStock.phoneVerified') }}
                </div>
              </form-group>

              <span class="field-helper-text mt-sm">
                {{ $t('Form.mandatoryFields') }}
              </span>

              <form-group
                v-if="shouldDisplayNewsletter && isBackInStockCommerceApiEnabled"
                :show-label="false"
                :attrs="fields.newsletter"
                class="mt-md"
              >
                <checkbox-field v-model="fields.newsletter.value" />
              </form-group>

              <hr class="divider" />
            </template>

            <template #form-actions="{ isDisabled }">
              <PhxsgButton
                v-if="showSendTokenButton"
                ref="sendTokenPhoneButton"
                :disabled="isDisabled || isSendingToken"
                variant="plain"
                color="primary"
                full
                type="button"
                data-cy="stock-notify-send-token-btn"
                @click="sendToken"
              >
                {{ tokenRequestPending ? $t('Form.backInStock.verify') : $t('Form.backInStock.sendVerificationCode') }}
              </PhxsgButton>
            </template>
          </phoenix-form>

          <send-phone-token-form
            v-if="isPhoneVerificationEnabled && isCnCountry && !isPhoneVerified"
            ref="sendPhoneTokenForm"
            :mobile-phone="formPhoneNumber"
            type="verify"
            :is-boxed="false"
            :show-errors-in-toast="true"
            action-route-name="api.customer.phoneValidation.send"
          />
        </div>

        <div v-show="!hasRegistrationSuccess && isCnCountry && isPhoneVerificationStep" class="box">
          <div class="h6">{{ $t('Form.backInStock.verifyPhoneTitle') }}</div>

          <div class="mt-md">
            {{
              $t('Form.backInStock.verifyPhoneDescription', {
                phone: fields.phoneNumber.value,
              })
            }}
          </div>

          <div class="mt-md" data-cy="stock-notify-token-form">
            <form-group :attrs="phoneToken" class="mt-md">
              <phone-token-field v-model="phoneToken.value" @resend-phone-token="onResendToken" />
            </form-group>

            <hr class="divider" />

            <div class="form-actions form-actions--stretch">
              <PhxsgButton
                variant="plain"
                color="primary"
                data-cy="stock-notify-verify-btn"
                @click.prevent.stop="verifyPhoneToken"
              >
                {{ $t('Form.backInStock.verify') }}
              </PhxsgButton>
            </div>

            <div class="form-actions form-actions--stretch mt-sm">
              <PhxsgButton variant="plain" color="secondary" data-cy="stock-notify-back-btn" @click="backToForm">
                {{ $t('Form.backInStock.backToForm') }}
              </PhxsgButton>
            </div>
          </div>
        </div>

        <div v-show="hasRegistrationSuccess" class="box">
          <div class="h6">{{ $t('Form.backInStock.thanks') }}</div>

          <div class="mt-md">{{ $t('Form.backInStock.thanksIntro') }}</div>

          <product-card-key
            v-if="hasProduct"
            class="mt-md"
            v-bind="product"
            :description="productDescription"
            :secondary-description="productSecondaryDescription"
          />

          <div class="mt-md">{{ $t('Form.backInStock.thanksEndMessage') }}</div>

          <hr class="divider" />

          <div class="form-actions form-actions--stretch">
            <PhxsgButton
              variant="plain"
              color="primary"
              :data-panel-close="id"
              :aria-label="$t('Accessibility.button.close')"
              data-cy="stock-notify-panel-close-btn"
            >
              {{ $t('Accessibility.button.close') }}
            </PhxsgButton>
          </div>
        </div>
      </template>
    </panel>
  </teleport>
</template>
